import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import { useId, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import type {
  FormFieldDescriptor,
  FormOptionDescriptor,
  ResolvedFormOption,
  TranslationKey,
} from '@finance/contracts';
import type { StaticTranslator } from '../../i18n/static-translator';
import {
  resolveTKey,
  translateRuntimeTKey,
  translateRuntimeTKeyOrFallback,
} from '../../i18n/resolve-key';
import { buildValidationRules } from './validation-registry';
import { shouldMaskInDisplay, maskValue } from './sensitive-value-policy';
import { useFieldOptions } from './use-field-options';
import { FileUploadAdapter } from '../../components/experience/forms/FileUploadAdapter';
import type { FieldAdapterProps, FieldAdapterDescriptor } from './field-adapter-registry';
import { normalizeDateInput, normalizeDateTimeInput } from '../dates/date-semantics';

const EXACT_DECIMAL_TYPES = new Set(['decimal', 'quantity', 'money']);

function resolveFieldRuntimeKey(key: TranslationKey | undefined, t: StaticTranslator): string | undefined {
  return key ? t(resolveTKey(key)) : undefined;
}

function resolveOptionLabel(option: FormOptionDescriptor, t: StaticTranslator): string {
  if (option.label !== undefined && option.label !== null && option.label !== '') {
    return option.label;
  }
  if (option.labelKey) return t(resolveTKey(option.labelKey));
  return String(option.value);
}

function getFieldLabel(field: FormFieldDescriptor, t: StaticTranslator): string {
  return t(resolveTKey(field.labelKey));
}

function fieldTypeToInputType(type: string): string {
  switch (type) {
    case 'number': return 'number';
    case 'date': return 'date';
    case 'datetime': return 'datetime-local';
    case 'password': return 'password';
    default: return 'text';
  }
}

function getErrorMessage(
  field: FormFieldDescriptor,
  formState: FieldAdapterProps['formState'],
  serverError: string | undefined,
  t: StaticTranslator,
): string | undefined {
  const fieldError = formState.errors[field.name];
  if (fieldError?.message) {
    // Server errors store the canonical translation key as the RHF message;
    // translate it before presentation. Client-side validation messages are
    // already translated by the validation registry.
    if (fieldError.type === 'server') {
      return translateRuntimeTKeyOrFallback(t, String(fieldError.message));
    }
    return String(fieldError.message);
  }
  if (serverError) return translateRuntimeTKeyOrFallback(t, serverError);
  return undefined;
}

function mergeDescribedBy(...ids: Array<string | undefined>): string | undefined {
  const value = ids.filter(Boolean).join(' ');
  return value || undefined;
}

function isFieldValid(
  field: FormFieldDescriptor,
  formState: FieldAdapterProps['formState'],
): boolean {
  const touched = formState.touchedFields[field.name];
  const hasError = Boolean(formState.errors[field.name]);
  return Boolean(touched) && !hasError;
}

function parseControlledValue(fieldType: string, value: string): unknown {
  if (value === '') return undefined;
  if (fieldType === 'number') {
    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? value : numberValue;
  }
  return value;
}

/**
 * Normalize date/datetime values to the format expected by HTML inputs.
 * Date inputs require `yyyy-MM-dd`; datetime-local inputs require `yyyy-MM-ddTHH:mm`.
 */
function normalizeDateInputValue(value: unknown, fieldType: string): unknown {
  return fieldType === 'date' ? normalizeDateInput(value) : normalizeDateTimeInput(value);
}

export function shouldForceInputLabelShrink(value: unknown, placeholder?: string): boolean {
  if (placeholder) return true;
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

function decimalInputProps(fieldType: string) {
  if (!EXACT_DECIMAL_TYPES.has(fieldType)) return {};
  return {
    inputMode: 'decimal' as const,
    pattern: '[+-]?[0-9]*([.][0-9]*)?',
    autoComplete: 'off',
    spellCheck: false,
  };
}

/**
 * Derive a sensible HTML autocomplete token from the field name and type.
 * This eliminates Chrome "Input elements should have autocomplete attributes"
 * warnings and gives users proper password-manager / autofill hints.
 */
function deriveAutoComplete(name: string, fieldType: string): string {
  if (fieldType === 'password') {
    if (name === 'currentPassword' || name === 'password') return 'current-password';
    if (name === 'newPassword') return 'new-password';
    if (name === 'confirmPassword') return 'new-password';
    return 'current-password';
  }
  if (EXACT_DECIMAL_TYPES.has(fieldType) || fieldType === 'number') return 'off';
  if (name === 'email') return 'email';
  if (name === 'phone' || name === 'phoneNumber' || name === 'mobile') return 'tel';
  if (name === 'firstName' || name === 'givenName') return 'given-name';
  if (name === 'lastName' || name === 'surname' || name === 'familyName') return 'family-name';
  if (name === 'address' || name === 'streetAddress') return 'street-address';
  if (name === 'city') return 'address-level2';
  if (name === 'postalCode') return 'postal-code';
  if (name === 'totpCode' || name === 'otp' || name === 'verificationCode') return 'one-time-code';
  if (name === 'username') return 'username';
  if (name === 'organization' || name === 'organizationName') return 'organization';
  return 'off';
}

const ValidAdornment = () => (
  <InputAdornment position="end">
    <CheckCircleOutlineIcon color="success" fontSize="small" />
  </InputAdornment>
);

const textAdapter = (props: FieldAdapterProps) => {
  const {
    field, state, register, control, formState, serverError, pending, t, formValues, describedBy,
  } = props;
  const label = getFieldLabel(field, t);
  const error = getErrorMessage(field, formState, serverError, t);
  const isDateLike = field.fieldType === 'date' || field.fieldType === 'datetime';
  const defaultValue = field.fieldType === 'password'
    ? undefined
    : (field.value?.defaultValue as string | number | undefined);
  const normalizedDefault = isDateLike
    ? normalizeDateInputValue(defaultValue, field.fieldType)
    : defaultValue;
  const rules = buildValidationRules(field, t, formValues);
  const disabled = state.disabled || pending;
  const controlledNumeric = field.fieldType === 'number' || EXACT_DECIMAL_TYPES.has(field.fieldType);
  const maskedDisplay = (shouldMaskInDisplay(field) || state.masked) && state.readonly;
  const placeholder = !isDateLike ? resolveFieldRuntimeKey(field.placeholderKey, t) : undefined;
  const helpText = resolveFieldRuntimeKey(field.helpTextKey, t);
  const helperText = error ?? helpText;
  const showValid = isFieldValid(field, formState);
  const currentValue = formValues?.[field.name];
  const forceInputLabelShrink = isDateLike || shouldForceInputLabelShrink(currentValue, placeholder);
  const autoComplete = deriveAutoComplete(field.name, field.fieldType);

  if (controlledNumeric && control) {
    return (
      <Controller
        control={control}
        name={field.name}
        rules={rules}
        defaultValue={defaultValue ?? ''}
        render={({ field: controllerField }) => (
          <TextField
            fullWidth
            type={field.fieldType === 'number' ? 'number' : 'text'}
            name={controllerField.name}
            inputRef={controllerField.ref}
            label={label}
            value={maskedDisplay ? maskValue(controllerField.value) : (controllerField.value ?? '')}
            disabled={disabled}
            error={Boolean(error)}
            helperText={helperText}
            aria-invalid={Boolean(error)}
            placeholder={placeholder}
            onChange={event => controllerField.onChange(parseControlledValue(field.fieldType, event.target.value))}
            onBlur={controllerField.onBlur}
            required={state.required}
            slotProps={{
              input: { endAdornment: showValid ? <ValidAdornment /> : undefined },
              ...(placeholder ? { inputLabel: { shrink: true } } : {}),
              htmlInput: {
                'aria-required': state.required,
                'aria-describedby': describedBy,
                readOnly: state.readonly || maskedDisplay,
                autoComplete,
                ...(field.fieldType === 'number'
                  ? { inputMode: 'numeric' as const, step: 1 }
                  : decimalInputProps(field.fieldType)),
              },
            }}
          />
        )}
      />
    );
  }

  return (
    <TextField
      fullWidth
      type={fieldTypeToInputType(field.fieldType)}
      label={label}
      required={state.required}
      defaultValue={maskedDisplay ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022' : normalizedDefault}
      disabled={disabled}
      error={Boolean(error)}
      helperText={helperText}
      aria-invalid={Boolean(error)}
      placeholder={placeholder}
      slotProps={{
        input: { endAdornment: showValid ? <ValidAdornment /> : undefined },
        ...(forceInputLabelShrink ? { inputLabel: { shrink: true } } : {}),
        htmlInput: {
          'aria-required': state.required,
          'aria-describedby': describedBy,
          readOnly: state.readonly || maskedDisplay,
          autoComplete,
          ...decimalInputProps(field.fieldType),
        },
      }}
      {...register(field.name, rules)}
    />
  );
};

const textareaAdapter = (props: FieldAdapterProps) => {
  const { field, state, register, formState, serverError, pending, t, formValues, describedBy } = props;
  const label = getFieldLabel(field, t);
  const error = getErrorMessage(field, formState, serverError, t);
  const defaultValue = field.value?.defaultValue as string | undefined;
  const rules = buildValidationRules(field, t, formValues);
  const disabled = state.disabled || pending;
  const placeholder = resolveFieldRuntimeKey(field.placeholderKey, t);
  const helpText = resolveFieldRuntimeKey(field.helpTextKey, t);
  const currentValue = formValues?.[field.name];
  const forceInputLabelShrink = shouldForceInputLabelShrink(currentValue, placeholder);
  const autoComplete = deriveAutoComplete(field.name, field.fieldType);

  return (
    <TextField
      fullWidth
      multiline
      minRows={3}
      label={label}
      required={state.required}
      defaultValue={defaultValue}
      disabled={disabled}
      error={Boolean(error)}
      helperText={error ?? helpText}
      aria-invalid={Boolean(error)}
      placeholder={placeholder}
      slotProps={{
        ...(forceInputLabelShrink ? { inputLabel: { shrink: true } } : {}),
        htmlInput: {
          'aria-required': state.required,
          'aria-describedby': describedBy,
          readOnly: state.readonly,
          autoComplete,
        },
      }}
      {...register(field.name, rules)}
    />
  );
};

function useResolvedOptions(
  field: FormFieldDescriptor,
  formValues: Record<string, unknown>,
  t: FieldAdapterProps['t'],
) {
  const dynamic = useFieldOptions(field, formValues, t);
  const options: ResolvedFormOption[] = field.options
    ? field.options.map(option => ({
        value: option.value,
        label: resolveOptionLabel(option, t),
        description: resolveFieldRuntimeKey(option.descriptionKey, t),
        disabled: option.disabled,
        metadata: option.metadata,
      }))
    : dynamic.options;
  return { ...dynamic, options };
}

function OptionContent({ option }: { option: ResolvedFormOption }) {
  return (
    <ListItemText
      primary={option.label}
      secondary={option.description}
      slotProps={{
        primary: { sx: { overflowWrap: 'anywhere' } },
        secondary: { sx: { overflowWrap: 'anywhere' } },
      }}
    />
  );
}

function SearchableSelect({
  field, state, control, formState, serverError, pending, t, formValues, describedBy, multiple,
}: FieldAdapterProps & { multiple: boolean }) {
  if (!control) return null;
  const label = getFieldLabel(field, t);
  const error = getErrorMessage(field, formState, serverError, t);
  const rules = buildValidationRules(field, t, formValues);
  const disabled = state.disabled || pending;
  const resolved = useResolvedOptions(field, formValues ?? {}, t);
  const placeholder = resolveFieldRuntimeKey(field.placeholderKey, t);
  const helpText = resolveFieldRuntimeKey(field.helpTextKey, t);
  const helperText = error ?? resolved.error ?? helpText;
  const minimumSearchLength = field.optionSource?.minimumSearchLength ?? 0;
  const [inputValue, setInputValue] = useState('');
  const searchReady = inputValue.length >= minimumSearchLength;
  const visibleOptions = useMemo(() => {
    if (!searchReady && minimumSearchLength > 0) return [];
    const query = inputValue.trim().toLocaleLowerCase();
    if (!query) return resolved.options;
    return resolved.options.filter(option =>
      `${option.label} ${option.description ?? ''}`.toLocaleLowerCase().includes(query),
    );
  }, [inputValue, minimumSearchLength, resolved.options, searchReady]);

  return (
    <Controller
      control={control}
      name={field.name}
      rules={rules}
      defaultValue={multiple ? (field.value?.defaultValue ?? []) : (field.value?.defaultValue ?? '')}
      render={({ field: controllerField }) => {
        const selectedValues = multiple
          ? (Array.isArray(controllerField.value) ? controllerField.value.map(String) : [])
          : [String(controllerField.value ?? '')].filter(Boolean);
        const selectedOptions = selectedValues.map(value =>
          resolved.options.find(option => String(option.value) === value) ?? { value, label: value },
        );
        const selected = multiple ? selectedOptions : (selectedOptions[0] ?? null);
        return (
          <Autocomplete
            multiple={multiple}
            options={visibleOptions}
            value={selected as never}
            inputValue={inputValue}
            onInputChange={(_event, nextInput, reason) => {
              if (reason !== 'reset') setInputValue(nextInput);
            }}
            onChange={(_event, nextValue) => {
              if (multiple) {
                controllerField.onChange((nextValue as ResolvedFormOption[]).map(option => option.value));
              } else {
                controllerField.onChange((nextValue as ResolvedFormOption | null)?.value ?? '');
              }
            }}
            onBlur={controllerField.onBlur}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, candidate) => String(option.value) === String(candidate.value)}
            getOptionDisabled={option => Boolean(option.disabled)}
            loading={resolved.loading}
            disabled={disabled}
            filterOptions={options => options}
            noOptionsText={
              !searchReady && minimumSearchLength > 0
                ? t('form.typeToSearch', { count: minimumSearchLength })
                : t('form.noOptionsAvailable')
            }
            renderOption={(optionProps, option) => (
              <Box component="li" {...optionProps} key={String(option.value)} sx={{ minWidth: 0 }}>
                <OptionContent option={option} />
              </Box>
            )}
            renderValue={
              multiple
                ? (values, getItemProps) =>
                    (Array.isArray(values) ? values : [values]).map((option, index) => {
                      const tagProps = getItemProps({ index });
                      return (
                        <Chip
                          {...tagProps}
                          key={String(option.value)}
                          label={option.label}
                          size="small"
                          sx={{ maxWidth: 220 }}
                        />
                      );
                    })
                : undefined
            }
            renderInput={params => (
              <TextField
                {...params}
                name={controllerField.name}
                inputRef={controllerField.ref}
                label={label}
                placeholder={placeholder}
                required={state.required}
                error={Boolean(error || resolved.error)}
                helperText={helperText}
                aria-invalid={Boolean(error || resolved.error)}
                slotProps={{
                  ...params.slotProps,
                  ...(placeholder ? { inputLabel: { shrink: true } } : {}),
                  htmlInput: {
                    ...params.slotProps?.htmlInput,
                    'aria-required': state.required,
                    'aria-describedby': mergeDescribedBy(
                      params.slotProps?.htmlInput?.['aria-describedby'] as string | undefined,
                      describedBy,
                    ),
                    autoComplete: 'off',
                  },
                  input: {
                    ...params.slotProps?.input,
                    endAdornment: (
                      <>
                        {resolved.loading && <CircularProgress color="inherit" size={18} />}
                        {params.slotProps?.input?.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        );
      }}
    />
  );
}

const SelectAdapter = (props: FieldAdapterProps) => {
  const { field, state, register, control, formState, serverError, pending, t, formValues, describedBy } = props;
  if (field.optionSource?.allowSearch) return <SearchableSelect {...props} multiple={false} />;
  const label = getFieldLabel(field, t);
  const error = getErrorMessage(field, formState, serverError, t);
  const defaultValue = (field.value?.defaultValue as string | undefined) ?? '';
  const rules = buildValidationRules(field, t, formValues);
  const disabled = state.disabled || pending;
  const labelId = useId();
  const resolved = useResolvedOptions(field, formValues ?? {}, t);
  const placeholder = resolveFieldRuntimeKey(field.placeholderKey, t);
  const helpText = resolveFieldRuntimeKey(field.helpTextKey, t);
  const hasError = Boolean(error || resolved.error);
  const helperText = error ?? resolved.error ?? helpText;

  const renderOptions = () => {
    if (resolved.loading) {
      return <MenuItem disabled><CircularProgress size={16} sx={{ mr: 1 }} /> {t('common.loading')}</MenuItem>;
    }
    if (resolved.empty && !field.options) {
      return <MenuItem disabled><ListItemText primary={t('form.noOptionsAvailable')} /></MenuItem>;
    }
    return resolved.options.map(option => (
      <MenuItem key={String(option.value)} value={String(option.value)} disabled={option.disabled}>
        <OptionContent option={option} />
      </MenuItem>
    ));
  };

  const selectDisplayProps = placeholder
    ? {
        displayEmpty: true,
        renderValue: (selected: unknown) =>
          selected === '' || selected === undefined
            ? <Typography component="span" color="text.secondary">{placeholder}</Typography>
            : resolved.options.find(option => String(option.value) === String(selected))?.label ?? String(selected),
      }
    : {};

  if (!control) {
    return (
      <FormControl fullWidth error={hasError} required={state.required}>
        <InputLabel id={labelId} shrink={placeholder ? true : undefined}>{label}</InputLabel>
        <Select
          labelId={labelId}
          label={label}
          defaultValue={defaultValue}
          disabled={disabled}
          aria-invalid={hasError}
          inputProps={{ 'aria-describedby': describedBy }}
          {...selectDisplayProps}
          {...register(field.name, rules)}
        >
          {placeholder && <MenuItem value="">{placeholder}</MenuItem>}
          {renderOptions()}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  return (
    <Controller
      control={control}
      name={field.name}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field: controllerField }) => (
        <FormControl fullWidth error={hasError} required={state.required}>
          <InputLabel id={labelId} shrink={placeholder ? true : undefined}>{label}</InputLabel>
          <Select
            {...controllerField}
            labelId={labelId}
            label={label}
            disabled={disabled}
            aria-invalid={hasError}
            inputProps={{ 'aria-describedby': describedBy }}
            {...selectDisplayProps}
          >
            {placeholder && <MenuItem value="">{placeholder}</MenuItem>}
            {renderOptions()}
          </Select>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      )}
    />
  );
};

const MultiselectAdapter = (props: FieldAdapterProps) => {
  const { field, state, register, control, formState, serverError, pending, t, formValues, describedBy } = props;
  if (field.optionSource?.allowSearch) return <SearchableSelect {...props} multiple />;
  const label = getFieldLabel(field, t);
  const error = getErrorMessage(field, formState, serverError, t);
  const defaultValue = (field.value?.defaultValue as string[] | undefined) ?? [];
  const rules = buildValidationRules(field, t, formValues);
  const disabled = state.disabled || pending;
  const labelId = useId();
  const resolved = useResolvedOptions(field, formValues ?? {}, t);
  const placeholder = resolveFieldRuntimeKey(field.placeholderKey, t);
  const helpText = resolveFieldRuntimeKey(field.helpTextKey, t);
  const hasError = Boolean(error || resolved.error);
  const helperText = error ?? resolved.error ?? helpText;

  const renderChips = (selected: string[]) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minWidth: 0 }}>
      {selected.length === 0 && placeholder && (
        <Typography component="span" color="text.secondary">{placeholder}</Typography>
      )}
      {selected.map(value => {
        const option = resolved.options.find(candidate => String(candidate.value) === value);
        return <Chip key={value} label={option?.label ?? value} size="small" sx={{ maxWidth: 220 }} />;
      })}
    </Box>
  );

  const renderOptions = () => {
    if (resolved.loading) {
      return <MenuItem disabled><CircularProgress size={16} sx={{ mr: 1 }} /> {t('common.loading')}</MenuItem>;
    }
    if (resolved.empty && !field.options) {
      return <MenuItem disabled><ListItemText primary={t('form.noOptionsAvailable')} /></MenuItem>;
    }
    return resolved.options.map(option => (
      <MenuItem key={String(option.value)} value={String(option.value)} disabled={option.disabled}>
        <OptionContent option={option} />
      </MenuItem>
    ));
  };

  const commonProps = {
    labelId,
    label,
    disabled,
    multiple: true,
    'aria-invalid': hasError,
    inputProps: { 'aria-describedby': describedBy },
    renderValue: (selected: unknown) => renderChips(selected as string[]),
  };

  if (!control) {
    return (
      <FormControl fullWidth error={hasError} required={state.required}>
        <InputLabel id={labelId} shrink={placeholder ? true : undefined}>{label}</InputLabel>
        <Select {...commonProps} defaultValue={defaultValue} {...register(field.name, rules)}>
          {renderOptions()}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  return (
    <Controller
      control={control}
      name={field.name}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field: controllerField }) => (
        <FormControl fullWidth error={hasError} required={state.required}>
          <InputLabel id={labelId} shrink={placeholder ? true : undefined}>{label}</InputLabel>
          <Select {...controllerField} {...commonProps}>
            {renderOptions()}
          </Select>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
      )}
    />
  );
};

function createBooleanAdapter(kind: 'checkbox' | 'switch') {
  return (props: FieldAdapterProps) => {
    const { field, state, register, control, formState, serverError, pending, t, formValues, describedBy } = props;
    const label = getFieldLabel(field, t);
    const error = getErrorMessage(field, formState, serverError, t);
    const helpText = resolveFieldRuntimeKey(field.helpTextKey, t);
    const helperText = error ?? helpText;
    const helperId = helperText ? `${field.name}-helper` : undefined;
    const ariaDescribedBy = mergeDescribedBy(describedBy, helperId);
    const defaultValue = Boolean(field.value?.defaultValue);
    const rules = buildValidationRules(field, t, formValues);
    const disabled = state.disabled || pending;
    const Control = kind === 'checkbox' ? Checkbox : Switch;

    if (!control) {
      return (
        <FormControl fullWidth component="fieldset" error={Boolean(error)} required={state.required}>
          <FormControlLabel
            control={
              <Control
                {...register(field.name, rules)}
                disabled={disabled}
                defaultChecked={defaultValue}
                aria-invalid={Boolean(error)}
                aria-describedby={ariaDescribedBy}
              />
            }
            label={label}
          />
          {helperText && <FormHelperText id={helperId}>{helperText}</FormHelperText>}
        </FormControl>
      );
    }

    return (
      <Controller
        control={control}
        name={field.name}
        rules={rules}
        defaultValue={defaultValue}
        render={({ field: controllerField }) => (
          <FormControl fullWidth component="fieldset" error={Boolean(error)} required={state.required}>
            <FormControlLabel
              control={
                <Control
                  {...controllerField}
                  checked={Boolean(controllerField.value)}
                  disabled={disabled}
                  aria-invalid={Boolean(error)}
                  aria-describedby={ariaDescribedBy}
                />
              }
              label={label}
            />
            {helperText && <FormHelperText id={helperId}>{helperText}</FormHelperText>}
          </FormControl>
        )}
      />
    );
  };
}

const checkboxAdapter = createBooleanAdapter('checkbox');
const switchAdapter = createBooleanAdapter('switch');
const hiddenAdapter = () => null;
const passwordAdapter = textAdapter;

const adapters: FieldAdapterDescriptor[] = [
  { fieldType: 'text', component: textAdapter },
  { fieldType: 'number', component: textAdapter },
  { fieldType: 'decimal', component: textAdapter },
  { fieldType: 'quantity', component: textAdapter },
  { fieldType: 'money', component: textAdapter },
  { fieldType: 'date', component: textAdapter },
  { fieldType: 'datetime', component: textAdapter },
  { fieldType: 'textarea', component: textareaAdapter },
  { fieldType: 'select', component: SelectAdapter },
  { fieldType: 'multiselect', component: MultiselectAdapter },
  { fieldType: 'checkbox', component: checkboxAdapter },
  { fieldType: 'switch', component: switchAdapter },
  { fieldType: 'hidden', component: hiddenAdapter },
  { fieldType: 'password', component: passwordAdapter },
  { fieldType: 'file', component: FileUploadAdapter },
];

const adapterMap = new Map<string, FieldAdapterDescriptor>(
  adapters.map(adapter => [adapter.fieldType, adapter]),
);

export function getFieldAdapter(fieldType: string): FieldAdapterDescriptor | undefined {
  return adapterMap.get(fieldType);
}

export function registerFieldAdapter(descriptor: FieldAdapterDescriptor): void {
  adapterMap.set(descriptor.fieldType, descriptor);
}

export { adapters as defaultFieldAdapters };
